import React, { useState } from "react";
import ArrowDownIcon from '@rsuite/icons/ArrowDown';
import { Form, Button, Tabs, Input, Message, Calendar, TimePicker, InputGroup, toaster } from "rsuite";
import { zhTW } from "rsuite/esm/locales";
import { useOrderCreation } from "../context/OrderCreationContext";
import { OrderType } from "../../../../../my-enum";
import { isFlightNumberValid, validateFlightInfo } from "../utils/flightHelpers";

const messageWarningPush = (strMessage: string) => (
  <Message showIcon type="warning" closable>
    {strMessage}
  </Message>
);

const getDefaultDate = () => {
  const now = new Date();
  now.setDate(now.getDate() + 2);
  return now;
};

const buildDateTime = (date: Date, time: Date | null) => {
  if (!time) {
    return null;
  }

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes()
  );
};

const validateBookingWindow = (dateTime: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = dateTime.valueOf() - today.valueOf();
  const diffInDays = diff / 1000 / 60 / 60 / 24;

  if (diffInDays < 2) {
    return "只能預約兩天後的日期，預訂當天或隔天請於官方LINE直言詢問";
  }

  if (diffInDays > 180) {
    return "只能預約六個月內的日期";
  }

  return null;
};

export const SetFlightInfo: React.FC = () => {
  const {
    orderType,
    setOrderType,
    setRideTime,
    setFlightSchedule,
    setFlightTime,
    setStep,
    selectedTerminal,
    setOpenAirportSelect,
    flightNo,
    setFlightNo,
    setIsRideTimeValid,
    setIsRidetimeTooLateOrEarly,
  } = useOrderCreation();

  const [rideDate, setRideDate] = useState<Date>(getDefaultDate);
  const [flightTimeInput, setFlightTimeInput] = useState<Date | null>(getDefaultDate);

  const showWarning = (message: string) => {
    toaster.push(messageWarningPush(message), {
      placement: 'topCenter',
      duration: 2000
    });
  };

  const handleNext = async (nextOrderType: OrderType) => {
    if (!selectedTerminal) {
      showWarning("請選擇機場");
      return;
    }

    if (!flightNo.trim()) {
      showWarning("請輸入航班編號");
      return;
    }

    if (!isFlightNumberValid(flightNo)) {
      showWarning("請輸入正確的航班號碼格式（例如：BR123）");
      return;
    }

    const selectedFlightDateTime = buildDateTime(rideDate, flightTimeInput);
    if (!selectedFlightDateTime) {
      showWarning(nextOrderType === OrderType.送機 ? "請選擇起飛時間" : "請選擇抵達時間");
      return;
    }

    const bookingWindowMessage = validateBookingWindow(selectedFlightDateTime);
    if (bookingWindowMessage) {
      showWarning(bookingWindowMessage);
      return;
    }

    const validationResult = await validateFlightInfo({
      orderType: nextOrderType,
      terminal: selectedTerminal.name,
      flightNo: flightNo.trim(),
      flightDate: selectedFlightDateTime,
    });

    if (validationResult.status !== 'valid') {
      showWarning(validationResult.message);
      return;
    }

    if (validationResult.message) {
      showWarning(validationResult.message);
    }

    setFlightSchedule(validationResult.schedule);
    setFlightTime(selectedFlightDateTime);
    setIsRideTimeValid(true);
    setIsRidetimeTooLateOrEarly(false);

    if (nextOrderType === OrderType.接機) {
      setRideTime(new Date(selectedFlightDateTime.getTime() + 30 * 60 * 1000));
    } else {
      setRideTime(selectedFlightDateTime);
    }

    setStep(1);
  };

  const renderFlightForm = (tabOrderType: OrderType) => {
    const isDeparture = tabOrderType === OrderType.送機;

    return (
      <Form style={{ paddingLeft: '15px', paddingRight: '15px' }} fluid>
        <Form.Group className="mb-3" style={{ textAlign: 'left' }}>
          <Form.ControlLabel>{isDeparture ? '送機機場' : '接機機場'}</Form.ControlLabel>
          <InputGroup inside>
            <Input
              inputMode="none"
              placeholder="選擇機場"
              value={selectedTerminal?.name}
              readOnly
              onClick={() => setOpenAirportSelect(true)}
            />
            <InputGroup.Addon>
              <ArrowDownIcon />
            </InputGroup.Addon>
          </InputGroup>
        </Form.Group>
        <Form.Group className="mb-3" style={{ textAlign: 'left' }}>
          <Form.ControlLabel>航班編號</Form.ControlLabel>
          <Input
            placeholder="輸入航班編號 例如: BR123"
            value={flightNo}
            onChange={(value) => setFlightNo(value.toUpperCase())}
          />
        </Form.Group>
        <br />
        <Form.Group className="mb-3" style={{ textAlign: 'left' }}>
          <Form.ControlLabel>{isDeparture ? '選擇起飛日期' : '選擇抵達日期'}</Form.ControlLabel>
          <Calendar
            compact
            value={rideDate}
            locale={zhTW.Calendar}
            onSelect={setRideDate}
          />
        </Form.Group>
        <br />
        <Form.Group className="mb-3" style={{ textAlign: 'left' }}>
          <Form.ControlLabel>{isDeparture ? '選擇起飛時間' : '選擇抵達時間'}</Form.ControlLabel>
          <TimePicker
            value={flightTimeInput}
            placeholder="選擇時間"
            hideMinutes={(minute) => minute % 5 !== 0}
            locale={zhTW.DateTimeFormats}
            editable={false}
            placement="topStart"
            block
            onChange={setFlightTimeInput}
          />
        </Form.Group>
        <br />
        <Form.Group className="d-grid">
          <Button
            appearance="primary"
            style={{ width: '100px' }}
            onClick={() => handleNext(tabOrderType)}
          >
            下一步
          </Button>
        </Form.Group>
      </Form>
    );
  };

  return (
    <Tabs
      activeKey={orderType === OrderType.送機 ? 'FlightOutTab' : 'FlightInTab'}
      appearance="subtle"
      onSelect={(eventKey) => {
        if (eventKey === 'FlightOutTab') {
          setOrderType(OrderType.送機);
        } else {
          setOrderType(OrderType.接機);
        }
      }}
    >
      <Tabs.Tab eventKey="FlightOutTab" title="送機">
        <br />
        {renderFlightForm(OrderType.送機)}
        <br />
      </Tabs.Tab>

      <Tabs.Tab eventKey="FlightInTab" title="接機">
        <br />
        {renderFlightForm(OrderType.接機)}
        <br />
      </Tabs.Tab>
    </Tabs>
  );
};
